// Captures attribution data (UTM params, gclid, referrer) on first visit.
// Stores in localStorage so it survives across page navigations and the
// React Router Link transition from `/` → `/register` that was stripping it.

const KEY_SOURCE = 'scn_utm_source';
const KEY_CAMPAIGN = 'scn_utm_campaign';
const KEY_MEDIUM = 'scn_utm_medium';
const KEY_GCLID = 'scn_gclid';
const KEY_RDT_CID = 'scn_rdt_cid';
const KEY_FBCLID = 'scn_fbclid';
const KEY_FBP = 'scn_fbp';
const KEY_FBC = 'scn_fbc';
const KEY_REF = 'scn_ref';
// First-touch landing page + referrer, which page the visitor FIRST arrived on
// (esp. an SEO /guides/... page), so we can see which content actually converts.
const KEY_LANDING = 'scn_landing_page';
const KEY_REFERRER = 'scn_referrer';

// Read a browser cookie by name (Meta's pixel sets _fbp / _fbc). Returns null
// if absent or if cookies are inaccessible.
function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}
const KEY_CAPTURED = 'scn_attribution_captured';
const KEY_CAPTURED_AT = 'scn_attribution_captured_at';

// Stored attribution expires after this many days. Without this, a flyer
// visit from months ago keeps polluting today's signup attribution, that
// happened with testmay28@gmail.com (2026-05-28), who got attributed to an
// old `ebay_flyer/stub_collectors` flyer visit instead of `direct`.
const ATTRIBUTION_TTL_DAYS = 30;
const ATTRIBUTION_TTL_MS = ATTRIBUTION_TTL_DAYS * 24 * 60 * 60 * 1000;

// Google Ads ValueTrack macros (e.g. {campaignid}) expand server-side at
// click time. If a tracking template uses a macro that doesn't exist -
// {campaignname} is a common mistake; only {campaignid} is real, Google
// passes the literal "{campaignname}" string through, polluting attribution
// (14 signups got campaign="{campaignname}" starting 2026-05-29). Treat any
// unexpanded "{...}" value as missing so the junk never gets stored.
function cleanMacro(v) {
  if (!v) return v;
  return /^\{.*\}$/.test(v.trim()) ? null : v;
}

function isAttributionExpired() {
  try {
    const ts = localStorage.getItem(KEY_CAPTURED_AT);
    if (!ts) return false; // no timestamp → assume fresh (back-compat for users who captured before this rollout)
    const captured = Date.parse(ts);
    if (!Number.isFinite(captured)) return false;
    return Date.now() - captured > ATTRIBUTION_TTL_MS;
  } catch {
    return false;
  }
}

export function captureAttribution() {
  try {
    // First, evict any expired attribution so the rest of this function
    // doesn't get fooled into treating it as already-captured.
    if (isAttributionExpired()) {
      clearAttribution();
    }

    const params = new URLSearchParams(window.location.search);
    const utm_source = cleanMacro(params.get('utm_source'));
    const utm_campaign = cleanMacro(params.get('utm_campaign'));
    const utm_medium = cleanMacro(params.get('utm_medium'));
    const gclid = params.get('gclid');
    // Reddit Ads click ID, required to attribute Stripe purchases back to
    // the original Reddit ad click (same pattern as gclid for Google Ads).
    const rdt_cid = params.get('rdt_cid');
    // Meta (Facebook/Instagram) Ads click ID, attribute Stripe purchases back
    // to the Meta ad click via the Conversions API (same pattern as gclid).
    const fbclid = params.get('fbclid');
    // Referral code, from a dealer's personalized flyer/link (?ref=CODE).
    const ref = params.get('ref');

    // If we already have a non-direct source captured, don't overwrite with a weaker signal.
    const alreadyCaptured = localStorage.getItem(KEY_CAPTURED) === '1';

    // Capture timestamp is set/refreshed whenever we set or update any
    // attribution field, so the TTL window restarts on each meaningful
    // touchpoint instead of expiring mid-funnel.
    const stamp = () => localStorage.setItem(KEY_CAPTURED_AT, new Date().toISOString());

    if (utm_source) {
      localStorage.setItem(KEY_SOURCE, utm_source);
      if (utm_campaign) localStorage.setItem(KEY_CAMPAIGN, utm_campaign);
      if (utm_medium) localStorage.setItem(KEY_MEDIUM, utm_medium);
      localStorage.setItem(KEY_CAPTURED, '1');
      stamp();
    } else if (gclid) {
      // Google Ads click with no UTM, still attribute to Google Ads
      localStorage.setItem(KEY_SOURCE, 'google_ads');
      localStorage.setItem(KEY_MEDIUM, 'cpc');
      localStorage.setItem(KEY_CAPTURED, '1');
      stamp();
    } else if (rdt_cid) {
      // Reddit Ads click with no UTM, attribute to Reddit Ads
      localStorage.setItem(KEY_SOURCE, 'reddit_ads');
      localStorage.setItem(KEY_MEDIUM, 'cpc');
      localStorage.setItem(KEY_CAPTURED, '1');
      stamp();
    } else if (fbclid) {
      // Meta Ads click with no UTM, attribute to Meta Ads
      localStorage.setItem(KEY_SOURCE, 'meta_ads');
      localStorage.setItem(KEY_MEDIUM, 'cpc');
      localStorage.setItem(KEY_CAPTURED, '1');
      stamp();
    } else if (ref) {
      // Referral link with no UTM, attribute the signup source to 'referral'
      localStorage.setItem(KEY_SOURCE, 'referral');
      localStorage.setItem(KEY_CAPTURED, '1');
      stamp();
    } else if (!alreadyCaptured && document.referrer) {
      try {
        const ref = new URL(document.referrer);
        // Ignore same-domain referrers (internal navigation)
        if (ref.hostname !== window.location.hostname) {
          let src;
          if (ref.hostname.includes('google')) src = 'google_organic';
          else if (ref.hostname.includes('reddit')) src = 'reddit_organic';
          else if (ref.hostname.includes('facebook') || ref.hostname.includes('fb.com')) src = 'facebook';
          else if (ref.hostname.includes('tiktok')) src = 'tiktok';
          else if (ref.hostname.includes('bing')) src = 'bing_organic';
          else src = ref.hostname;
          localStorage.setItem(KEY_SOURCE, src);
          localStorage.setItem(KEY_CAPTURED, '1');
          stamp();
        }
      } catch {}
    }

    // First-touch landing page + referrer (first visit wins; survives to signup).
    if (!localStorage.getItem(KEY_LANDING)) {
      try {
        localStorage.setItem(KEY_LANDING, (window.location.pathname || '/').slice(0, 300));
        if (document.referrer) localStorage.setItem(KEY_REFERRER, document.referrer.slice(0, 500));
        stamp();
      } catch {}
    }

    if (gclid) { localStorage.setItem(KEY_GCLID, gclid); stamp(); }
    if (rdt_cid) { localStorage.setItem(KEY_RDT_CID, rdt_cid); stamp(); }
    if (fbclid) { localStorage.setItem(KEY_FBCLID, fbclid); stamp(); }
    if (ref) { localStorage.setItem(KEY_REF, ref); stamp(); }

    // Meta pixel cookies (_fbp always, _fbc when the visitor arrived via an ad).
    // They're the strongest CAPI match keys, so snapshot them at capture time -
    // the pixel may set _fbc slightly after this runs, so getAttribution() also
    // re-reads them live at signup.
    const fbp = readCookie('_fbp');
    const fbc = readCookie('_fbc');
    if (fbp) { localStorage.setItem(KEY_FBP, fbp); }
    if (fbc) { localStorage.setItem(KEY_FBC, fbc); }
  } catch {}
}

export function getAttribution() {
  // Primary source: localStorage (survives page navigation), but only if
  // it isn't stale, a flyer visit from 4 months ago shouldn't get credit
  // for today's signup.
  let stored = {};
  if (!isAttributionExpired()) {
    try {
      stored = {
        signupSource: localStorage.getItem(KEY_SOURCE),
        signupCampaign: localStorage.getItem(KEY_CAMPAIGN),
        signupMedium: localStorage.getItem(KEY_MEDIUM),
        gclid: localStorage.getItem(KEY_GCLID),
        rdt_cid: localStorage.getItem(KEY_RDT_CID),
        fbclid: localStorage.getItem(KEY_FBCLID),
        fbp: localStorage.getItem(KEY_FBP),
        fbc: localStorage.getItem(KEY_FBC),
        ref: localStorage.getItem(KEY_REF),
        signupLandingPage: localStorage.getItem(KEY_LANDING),
        signupReferrer: localStorage.getItem(KEY_REFERRER),
      };
    } catch {}
  }

  // Fallback: in-app browsers (Reddit, Instagram, etc.) sometimes block or
  // wipe localStorage, which would drop ad attribution entirely. Read the
  // current URL directly so a click that lands on /register?utm_source=...
  // still gets attributed even when storage fails.
  let url = {};
  try {
    const p = new URLSearchParams(window.location.search);
    url = {
      signupSource: cleanMacro(p.get('utm_source')),
      signupCampaign: cleanMacro(p.get('utm_campaign')),
      signupMedium: cleanMacro(p.get('utm_medium')),
      gclid: p.get('gclid'),
      rdt_cid: p.get('rdt_cid'),
      fbclid: p.get('fbclid'),
      ref: p.get('ref'),
    };
    if (!url.signupSource && url.gclid) {
      url.signupSource = 'google_ads';
      url.signupMedium = url.signupMedium || 'cpc';
    }
    if (!url.signupSource && url.rdt_cid) {
      url.signupSource = 'reddit_ads';
      url.signupMedium = url.signupMedium || 'cpc';
    }
    if (!url.signupSource && url.fbclid) {
      url.signupSource = 'meta_ads';
      url.signupMedium = url.signupMedium || 'cpc';
    }
    if (!url.signupSource && url.ref) {
      url.signupSource = 'referral';
    }
  } catch {}

  // Meta cookies read LIVE at signup, the pixel may set _fbc after the initial
  // capture ran, so prefer a fresh cookie over the stored snapshot.
  const liveFbp = readCookie('_fbp');
  const liveFbc = readCookie('_fbc');

  return {
    signupSource: stored.signupSource || url.signupSource || 'direct',
    signupCampaign: stored.signupCampaign || url.signupCampaign || null,
    signupMedium: stored.signupMedium || url.signupMedium || null,
    gclid: stored.gclid || url.gclid || null,
    rdt_cid: stored.rdt_cid || url.rdt_cid || null,
    fbclid: stored.fbclid || url.fbclid || null,
    fbp: liveFbp || stored.fbp || null,
    fbc: liveFbc || stored.fbc || null,
    ref: stored.ref || url.ref || null,
    // Prefer the stored first-touch page; fall back to the current path only if
    // nothing was captured (better than null, though it may be /register).
    signupLandingPage: stored.signupLandingPage || (() => { try { return window.location.pathname; } catch { return null; } })(),
    signupReferrer: stored.signupReferrer || (() => { try { return document.referrer || null; } catch { return null; } })(),
  };
}

// True when the page is running inside a social app's in-app browser
// (Reddit, Instagram, Facebook, TikTok, etc.). Google OAuth is blocked in
// these webviews, so the signup page hides the Google button when true.
export function isInAppBrowser() {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    const sigs = [
      'fban', 'fbav', 'fb_iab',              // Facebook / Messenger
      'instagram',                            // Instagram
      'twitter',                              // Twitter / X
      'snapchat',                             // Snapchat
      'tiktok', 'musical_ly', 'bytedance',    // TikTok
      'pinterest',                            // Pinterest
      'linkedinapp',                          // LinkedIn
      'line/',                                // Line
      'reddit',                               // Reddit
    ];
    if (sigs.some((s) => ua.includes(s))) return true;
    // Generic Android in-app WebView marker
    if (ua.includes('; wv)') || ua.includes('; wv ')) return true;
    return false;
  } catch {
    return false;
  }
}

export function clearAttribution() {
  localStorage.removeItem(KEY_SOURCE);
  localStorage.removeItem(KEY_CAMPAIGN);
  localStorage.removeItem(KEY_MEDIUM);
  localStorage.removeItem(KEY_GCLID);
  localStorage.removeItem(KEY_RDT_CID);
  localStorage.removeItem(KEY_REF);
  localStorage.removeItem(KEY_LANDING);
  localStorage.removeItem(KEY_REFERRER);
  localStorage.removeItem(KEY_CAPTURED);
  localStorage.removeItem(KEY_CAPTURED_AT);
}
